import * as Popover from '@radix-ui/react-popover';
import * as Primitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp, Search } from 'lucide-react';
import {
  Children,
  Fragment,
  isValidElement,
  type ReactNode,
  type SelectHTMLAttributes,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

type Option = { value: string; label: ReactNode; text: string; disabled?: boolean };

/**
 * A list this long is faster to type at than to scroll, so it gets a filter
 * field. Below it, a search box is furniture: the whole list is already on
 * screen. Repeat, status and event type stay plain; tasks, notes, people and
 * the calendar's year stop being a scrolling hunt.
 */
const searchableFrom = 8;

/** An option's label as text, for filtering and for the trigger's title. */
function optionText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(optionText).join('');
  }

  if (isValidElement<{ children?: ReactNode }>(node)) {
    return optionText(node.props.children);
  }

  return '';
}

export function selectOptions(children: ReactNode): Option[] {
  return Children.toArray(children).flatMap(child => {
    if (
      !isValidElement<{ value?: string | number; children?: ReactNode; disabled?: boolean }>(child)
    ) {
      return [];
    }

    if (child.type === Fragment || child.type === 'optgroup') {
      return selectOptions(child.props.children);
    }

    return child.type === 'option'
      ? [
          {
            value: String(child.props.value ?? child.props.children ?? ''),
            label: child.props.children,
            text: optionText(child.props.children),
            disabled: child.props.disabled,
          },
        ]
      : [];
  });
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & {
  variant?: 'default' | 'inline' | 'cell';
  displayValue?: ReactNode;
};

/**
 * The one dropdown. Short lists open as a plain menu; long ones open with a
 * filter field. Either way the call site writes `<option>` children and the
 * hidden native `<select>` keeps form validation and the `onChange` contract,
 * so nothing downstream can tell which of the two it got.
 */
export function Select(props: SelectProps) {
  return selectOptions(props.children).length >= searchableFrom ? (
    <SearchableSelect {...props} />
  ) : (
    <PlainSelect {...props} />
  );
}

/** Class list for the trigger, shared by both variants so they look alike. */
function triggerClass(variant: SelectProps['variant'], className: string) {
  const shape =
    variant === 'inline' ? 'ui-input--inline' : variant === 'cell' ? 'ui-input--cell' : '';

  return `ui-select ${shape} ${className}`;
}

function PlainSelect({
  className = '',
  variant = 'default',
  displayValue,
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  id,
  autoFocus,
  title,
  ...props
}: SelectProps) {
  const native = useRef<HTMLSelectElement>(null),
    trigger = useRef<HTMLButtonElement>(null);

  const options = selectOptions(children);
  const [local, setLocal] = useState(String(defaultValue ?? options[0]?.value ?? ''));
  const current = String(value ?? local);

  return (
    <Primitive.Root
      value={`value:${current}`}
      disabled={disabled}
      onValueChange={encoded => {
        const next = encoded.slice(6);

        setLocal(next);

        if (native.current) {
          native.current.value = next;
          native.current.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }}
    >
      <Primitive.Trigger
        ref={trigger}
        id={id}
        autoFocus={autoFocus}
        title={title}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']}
        aria-required={required}
        className={triggerClass(variant, className)}
      >
        <Primitive.Value>
          {displayValue ?? options.find(option => option.value === current)?.label ?? 'Choose…'}
        </Primitive.Value>
        <Primitive.Icon>
          <ChevronDown aria-hidden="true" size={16} />
        </Primitive.Icon>
      </Primitive.Trigger>
      <select
        {...props}
        ref={native}
        className="ui-native-value"
        aria-hidden="true"
        tabIndex={-1}
        value={current}
        disabled={disabled}
        required={required}
        onInvalid={event => {
          event.preventDefault();
          trigger.current?.focus();
        }}
        onChange={event => {
          setLocal(event.target.value);
          onChange?.(event);
        }}
      >
        {children}
      </select>
      <Primitive.Portal>
        <Primitive.Content
          className="ui-select-menu"
          position="popper"
          sideOffset={6}
          collisionPadding={12}
        >
          <Primitive.ScrollUpButton className="ui-select-scroll">
            <ChevronUp size={16} />
          </Primitive.ScrollUpButton>
          <Primitive.Viewport>
            {options.map(option => (
              <Primitive.Item
                className="ui-select-option"
                key={option.value}
                value={`value:${option.value}`}
                disabled={option.disabled}
              >
                <Primitive.ItemText>{option.label}</Primitive.ItemText>
                <Primitive.ItemIndicator>
                  <Check size={16} />
                </Primitive.ItemIndicator>
              </Primitive.Item>
            ))}
          </Primitive.Viewport>
          <Primitive.ScrollDownButton className="ui-select-scroll">
            <ChevronDown size={16} />
          </Primitive.ScrollDownButton>
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );
}

/**
 * The same control with a filter field.
 *
 * Built on Popover rather than Select: Radix's Select owns the keyboard for
 * its own first-letter typeahead, and a text field inside it fights that for
 * every keystroke. Here the field holds focus the whole time the list is open
 * and drives it through `aria-activedescendant`, which is the combobox
 * pattern and what a screen reader expects.
 */
function SearchableSelect({
  className = '',
  variant = 'default',
  displayValue,
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  id,
  autoFocus,
  title,
  ...props
}: SelectProps) {
  const native = useRef<HTMLSelectElement>(null),
    trigger = useRef<HTMLButtonElement>(null),
    list = useRef<HTMLDivElement>(null);

  const listId = useId();

  const options = selectOptions(children);
  const [local, setLocal] = useState(String(defaultValue ?? options[0]?.value ?? ''));
  const current = String(value ?? local);

  const [open, setOpen] = useState(false),
    [query, setQuery] = useState(''),
    [active, setActive] = useState(0);

  const needle = query.trim().toLowerCase();

  const matches = needle
    ? options.filter(option => option.text.toLowerCase().includes(needle))
    : options;

  const chosen = options.find(option => option.value === current);

  /// Opening lands on what is already chosen rather than at the top, so the
  /// list opens showing where you are. Done here rather than in an effect
  /// keyed on `open`, which would also have to re-run on every keystroke and
  /// fight the arrow keys for the highlight.
  function toggle(next: boolean) {
    setOpen(next);

    if (next) {
      setQuery('');

      setActive(
        Math.max(
          0,
          options.findIndex(option => option.value === current),
        ),
      );
    }
  }

  // Keep the highlighted row in view while arrowing through a long list.
  useEffect(() => {
    list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  function choose(option: Option) {
    if (option.disabled) {
      return;
    }

    setLocal(option.value);
    toggle(false);

    if (native.current) {
      native.current.value = option.value;
      native.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  function navigate(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();

      if (matches.length) {
        const step = event.key === 'ArrowDown' ? 1 : -1;

        setActive((active + step + matches.length) % matches.length);
      }

      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();

      if (matches[active]) {
        choose(matches[active]);
      }

      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      setActive(event.key === 'Home' ? 0 : matches.length - 1);
    }
  }

  return (
    <Popover.Root open={open} onOpenChange={toggle}>
      <Popover.Trigger
        ref={trigger}
        id={id}
        type="button"
        autoFocus={autoFocus}
        title={title ?? chosen?.text}
        disabled={disabled}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        aria-describedby={props['aria-describedby']}
        aria-invalid={props['aria-invalid']}
        aria-required={required}
        className={triggerClass(variant, className)}
      >
        <span>{displayValue ?? chosen?.label ?? 'Choose…'}</span>
        <span>
          <ChevronDown aria-hidden="true" size={16} />
        </span>
      </Popover.Trigger>
      <select
        {...props}
        ref={native}
        className="ui-native-value"
        aria-hidden="true"
        tabIndex={-1}
        value={current}
        disabled={disabled}
        required={required}
        onInvalid={event => {
          event.preventDefault();
          trigger.current?.focus();
        }}
        onChange={event => {
          setLocal(event.target.value);
          onChange?.(event);
        }}
      >
        {children}
      </select>
      <Popover.Portal>
        <Popover.Content
          className="ui-select-menu ui-select-menu--searchable"
          align="start"
          sideOffset={6}
          collisionPadding={12}
        >
          <div className="ui-select-search">
            <Search aria-hidden="true" size={15} />
            <input
              // biome-ignore lint/a11y/noAutofocus: the popover exists to be typed in; opening it and leaving focus behind is the defect being fixed.
              autoFocus
              type="text"
              role="combobox"
              aria-expanded="true"
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={matches[active] ? `${listId}-${active}` : undefined}
              aria-label={`Filter ${props['aria-label'] ?? 'options'}`}
              placeholder="Type to filter…"
              value={query}
              onChange={event => {
                setQuery(event.target.value);
                setActive(0);
              }}
              onKeyDown={navigate}
            />
          </div>
          <div className="ui-select-list" id={listId} role="listbox" ref={list}>
            {matches.map((option, index) => (
              <button
                type="button"
                data-ui="specialized"
                data-index={index}
                data-highlighted={index === active ? '' : undefined}
                data-state={option.value === current ? 'checked' : undefined}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={option.value === current}
                className="ui-select-option"
                key={option.value}
                disabled={option.disabled}
                // Pointer movement highlights, so the mouse and the arrow keys
                // never disagree about which row Enter would take.
                onMouseMove={() => setActive(index)}
                onClick={() => choose(option)}
              >
                <span>{option.label}</span>
                {/* Always two children, like the Radix item, so the shared
                    option styling applies to both variants unchanged. */}
                <span>{option.value === current && <Check size={16} />}</span>
              </button>
            ))}
            {!matches.length && <p className="ui-select-empty">Nothing matches “{query}”.</p>}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
